<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="fetchReport">
            <div class="row justify-content-center align-items-center w-100">
              <div
                v-if="hasFilterErrors"
                class="col-12 filter_errors_summary"
              >
                <i class="fal fa-exclamation-circle"></i>
                <ul>
                  <li
                    v-for="(message, field) in activeFilterErrors"
                    :key="field"
                  >
                    {{ message }}
                  </li>
                </ul>
              </div>

              <!-- Start:: Period Select -->
              <div
                class="col-lg-4 filter_field_col"
                :class="{ 'has-error': filterErrors.period }"
              >
                <base-select-input
                  col="12"
                  :optionsList="periodOptions"
                  :placeholder="$t('REPORTS.timePeriod')"
                  v-model="filterOptions.period"
                  @fireInputEvent="clearFilterError('period')"
                />
                <p v-if="filterErrors.period" class="filter_field_error">
                  {{ filterErrors.period }}
                </p>
              </div>
              <!-- End:: Period Select -->

              <!-- Start:: Custom Date From -->
              <div
                v-if="
                  filterOptions.period && filterOptions.period.value === 'custom'
                "
                class="col-lg-4 filter_field_col"
                :class="{ 'has-error': filterErrors.from_date }"
              >
                <base-picker-input
                  col="12"
                  type="date"
                  :placeholder="$t('REPORTS.fromDate')"
                  v-model="filterOptions.from_date"
                  @input="clearFilterError('from_date')"
                />
                <p v-if="filterErrors.from_date" class="filter_field_error">
                  {{ filterErrors.from_date }}
                </p>
              </div>
              <!-- End:: Custom Date From -->

              <!-- Start:: Custom Date To -->
              <div
                v-if="
                  filterOptions.period && filterOptions.period.value === 'custom'
                "
                class="col-lg-4 filter_field_col"
                :class="{ 'has-error': filterErrors.to_date }"
              >
                <base-picker-input
                  col="12"
                  type="date"
                  :placeholder="$t('REPORTS.toDate')"
                  v-model="filterOptions.to_date"
                  @input="clearFilterError('to_date')"
                />
                <p v-if="filterErrors.to_date" class="filter_field_error">
                  {{ filterErrors.to_date }}
                </p>
              </div>
              <!-- End:: Custom Date To -->

              <!-- Start:: Driver Select -->
              <div
                class="col-lg-4 filter_field_col"
                :class="{ 'has-error': filterErrors.driver_id }"
              >
                <base-select-input
                  col="12"
                  :optionsList="driversList"
                  :placeholder="$t('REPORTS.driver')"
                  v-model="filterOptions.driver"
                  @fireInputEvent="clearFilterError('driver_id')"
                />
                <p v-if="filterErrors.driver_id" class="filter_field_error">
                  {{ filterErrors.driver_id }}
                </p>
              </div>
              <!-- End:: Driver Select -->

              <!-- Start:: City Input -->
              <div
                class="col-lg-4 filter_field_col"
                :class="{ 'has-error': filterErrors.city }"
              >
                <base-input
                  col="12"
                  type="text"
                  :placeholder="$t('REPORTS.city')"
                  v-model.trim="filterOptions.city"
                  @input="clearFilterError('city')"
                />
                <p v-if="filterErrors.city" class="filter_field_error">
                  {{ filterErrors.city }}
                </p>
              </div>
              <!-- End:: City Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" type="submit" :disabled="loading">
                <i
                  class="fal"
                  :class="loading ? 'fa-spinner fa-spin' : 'fa-chart-bar'"
                ></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="loading"
                @click="resetFilters"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Page Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.reports") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Page Title =========== -->

      <!-- Start:: Date Range Warning -->
      <div v-if="showDateRangeWarning" class="alert_warning">
        <i class="fal fa-exclamation-triangle mr-2"></i>
        {{ $t("REPORTS.dateRangeWarning") }}
      </div>
      <!-- End:: Date Range Warning -->

      <!-- Start:: Loading State -->
      <div v-if="loading && !reportLoaded" class="reports_loading">
        <i class="fal fa-spinner fa-spin"></i>
        <p>{{ $t("REPORTS.loadingReport") }}</p>
      </div>
      <!-- End:: Loading State -->

      <!-- Start:: Report Content -->
      <div v-if="reportLoaded" class="reports_content">
        <!--  =========== Start:: Export Buttons =========== -->
        <div v-if="hasData" class="export_wrapper">
          <button
            class="reports_btn reports_btn--success"
            @click="exportReport('excel')"
            :disabled="!hasData || exporting"
          >
            <i class="fal fa-file-excel mr-2"></i>
            {{ $t("REPORTS.exportExcel") }}
          </button>
          <button
            class="reports_btn reports_btn--danger"
            @click="exportReport('pdf')"
            :disabled="!hasData || exporting"
          >
            <i class="fal fa-file-pdf mr-2"></i>
            {{ $t("REPORTS.exportPdf") }}
          </button>
        </div>
        <!--  =========== End:: Export Buttons =========== -->

        <!--  =========== Start:: Statistics Cards =========== -->
        <div v-if="hasData" class="stats_grid">
          <div class="stat_card stat_card--total">
            <div class="stat_icon">
              <i class="fas fa-boxes"></i>
            </div>
            <div class="stat_info">
              <span class="stat_label">{{ $t("REPORTS.totalPackages") }}</span>
              <span class="stat_value">{{
                reportData.metrics.total_packages || 0
              }}</span>
            </div>
          </div>

          <div class="stat_card stat_card--delivered">
            <div class="stat_icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="stat_info">
              <span class="stat_label">{{
                $t("REPORTS.deliveredPackages")
              }}</span>
              <span class="stat_value">{{
                reportData.metrics.delivered || 0
              }}</span>
            </div>
          </div>

          <div class="stat_card stat_card--progress">
            <div class="stat_icon">
              <i class="fas fa-shipping-fast"></i>
            </div>
            <div class="stat_info">
              <span class="stat_label">{{
                $t("REPORTS.inProgressPackages")
              }}</span>
              <span class="stat_value">{{
                reportData.metrics.in_progress || 0
              }}</span>
            </div>
          </div>

          <div class="stat_card stat_card--delayed">
            <div class="stat_icon">
              <i class="fas fa-clock"></i>
            </div>
            <div class="stat_info">
              <span class="stat_label">{{
                $t("REPORTS.delayedPackages")
              }}</span>
              <span class="stat_value">{{
                reportData.metrics.delayed || 0
              }}</span>
            </div>
          </div>

          <div class="stat_card stat_card--rate">
            <div class="stat_icon">
              <i class="fas fa-chart-pie"></i>
            </div>
            <div class="stat_info">
              <span class="stat_label">{{ $t("REPORTS.completionRate") }}</span>
              <span class="stat_value"
                >{{ reportData.metrics.completion_rate || 0 }}%</span
              >
            </div>
          </div>

          <div class="stat_card stat_card--time">
            <div class="stat_icon">
              <i class="fas fa-stopwatch"></i>
            </div>
            <div class="stat_info">
              <span class="stat_label">{{
                $t("REPORTS.avgDeliveryTime")
              }}</span>
              <span class="stat_value">{{
                reportData.metrics.avg_delivery_time_hours
                  ? reportData.metrics.avg_delivery_time_hours +
                    " " +
                    $t("REPORTS.hours")
                  : "-"
              }}</span>
            </div>
          </div>
        </div>
        <!--  =========== End:: Statistics Cards =========== -->

        <!--  =========== Start:: Charts Section =========== -->
        <div class="charts_grid" v-if="hasData">
          <!-- Start:: Status By Days Chart -->
          <div class="chart_wrapper">
            <line-chart
              :key="'line-' + chartKey"
              :ChartLabels="statusByDaysLabels"
              :ChartData="statusByDaysSeries"
            >
              <template #chart_title>
                {{ $t("REPORTS.statusByDays") }}
              </template>
            </line-chart>
          </div>
          <!-- End:: Status By Days Chart -->

          <!-- Start:: Driver Performance Chart -->
          <div class="chart_wrapper">
            <column-chart
              :key="'col-' + chartKey"
              :ChartLabels="driverPerformanceLabels"
              :ChartData="driverPerformanceSeries"
            >
              <template #chart_title>
                {{ $t("REPORTS.driverPerformance") }}
              </template>
            </column-chart>
          </div>
          <!-- End:: Driver Performance Chart -->

          <!-- Start:: Geographic Distribution Chart -->
          <div class="chart_wrapper">
            <donut-chart
              :key="'donut-' + chartKey"
              :ChartLabels="regionDistributionLabels"
              :ChartData="regionDistributionData"
            >
              <template #chart_title>
                {{ $t("REPORTS.geographicDistribution") }}
              </template>
            </donut-chart>
          </div>
          <!-- End:: Geographic Distribution Chart -->
        </div>
        <!--  =========== End:: Charts Section =========== -->

        <!-- Start:: No Data Message -->
        <div v-if="!hasData" class="no_data_message">
          <img
            src="@/assets/media/illustrations/4.svg"
            alt=""
            class="no_data_illustration"
          />
          <p class="no_data_title">{{ $t("REPORTS.noData") }}</p>
          <p class="no_data_hint">{{ $t("REPORTS.noDataHint") }}</p>
        </div>
        <!-- End:: No Data Message -->
      </div>
      <!-- End:: Report Content -->
    </main>
    <!-- End:: Main Section -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import LineChart from "@/components/charts/LineChart.vue";
import ColumnChart from "@/components/charts/ColumnChart.vue";
import DonutChart from "@/components/charts/DonutChart.vue";

export default {
  name: "ReportsPage",

  components: {
    LineChart,
    ColumnChart,
    DonutChart,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    periodOptions() {
      return [
        {
          id: "today",
          name: this.$t("REPORTS.today"),
          value: "today",
        },
        {
          id: "week",
          name: this.$t("REPORTS.week"),
          value: "week",
        },
        {
          id: "month",
          name: this.$t("REPORTS.month"),
          value: "month",
        },
        {
          id: "custom",
          name: this.$t("REPORTS.custom"),
          value: "custom",
        },
      ];
    },

    hasData() {
      return (
        this.reportData.metrics && this.reportData.metrics.total_packages > 0
      );
    },

    showDateRangeWarning() {
      if (
        this.filterOptions.period &&
        this.filterOptions.period.value === "custom" &&
        this.filterOptions.from_date &&
        this.filterOptions.to_date
      ) {
        const from = new Date(this.filterOptions.from_date);
        const to = new Date(this.filterOptions.to_date);
        const diffTime = Math.abs(to - from);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 365;
      }
      return false;
    },

    // Charts computed data
    statusByDaysLabels() {
      if (!this.reportData.charts?.statuses_by_day) return [];
      return this.reportData.charts.statuses_by_day.map((item) => item.date);
    },

    statusByDaysSeries() {
      if (!this.reportData.charts?.statuses_by_day) return [];
      return [
        {
          name: this.$t("REPORTS.totalPackages"),
          data: this.reportData.charts.statuses_by_day.map(
            (item) => item.total || 0
          ),
        },
      ];
    },

    driverPerformanceLabels() {
      if (!this.reportData.charts?.driver_performance?.length) return [];
      return this.reportData.charts.driver_performance.map(
        (item) => item.driver_name
      );
    },

    driverPerformanceSeries() {
      if (!this.reportData.charts?.driver_performance?.length) return [];
      return [
        {
          name: this.$t("REPORTS.deliveredCount"),
          data: this.reportData.charts.driver_performance.map(
            (item) => item.count || 0
          ),
        },
      ];
    },

    regionDistributionLabels() {
      if (!this.reportData.charts?.city_distribution?.length) return [];
      return this.reportData.charts.city_distribution.map((item) => item.city);
    },

    regionDistributionData() {
      if (!this.reportData.charts?.city_distribution?.length) return [];
      return this.reportData.charts.city_distribution.map(
        (item) => item.count || 0
      );
    },

    hasFilterErrors() {
      return Object.values(this.filterErrors).some(Boolean);
    },

    activeFilterErrors() {
      return Object.keys(this.filterErrors).reduce((errors, field) => {
        if (this.filterErrors[field]) {
          errors[field] = this.filterErrors[field];
        }
        return errors;
      }, {});
    },
  },

  data() {
    return {
      loading: false,
      exporting: false,
      reportLoaded: false,
      chartKey: 0,
      filterFormIsActive: false,
      driversList: [],

      filterOptions: {
        period: null,
        from_date: null,
        to_date: null,
        driver: null,
        city: null,
      },

      filterErrors: {
        period: null,
        from_date: null,
        to_date: null,
        driver_id: null,
        city: null,
      },

      reportData: {
        metrics: {
          total_packages: 0,
          delivered: 0,
          in_progress: 0,
          delayed: 0,
          completion_rate: 0,
          avg_delivery_time_hours: 0,
        },
        charts: {
          statuses_by_day: [],
          driver_performance: [],
          city_distribution: [],
        },
      },
    };
  },

  mounted() {
    this.filterOptions.period = this.getDefaultPeriod();
    this.fetchDrivers();
    this.fetchReport();
  },

  methods: {
    async fetchDrivers() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "packages/drivers",
        });

        this.driversList = res.data.data || [];
      } catch (error) {
        console.error(error.response?.data?.message || error);
      }
    },

    getDefaultPeriod() {
      return this.periodOptions.find((option) => option.value === "today");
    },

    getEmptyFilterErrors() {
      return {
        period: null,
        from_date: null,
        to_date: null,
        driver_id: null,
        city: null,
      };
    },

    clearFilterErrors() {
      this.filterErrors = this.getEmptyFilterErrors();
    },

    clearFilterError(field) {
      if (this.filterErrors[field]) {
        this.$set(this.filterErrors, field, null);
      }
    },

    handleFilterApiError(error) {
      this.clearFilterErrors();

      if (!error.response) {
        this.$message.error(this.$t("REPORTS.fetchError"));
        return;
      }

      const errorData = error.response.data || {};

      if (
        errorData.errors &&
        typeof errorData.errors === "object" &&
        !Array.isArray(errorData.errors)
      ) {
        Object.keys(errorData.errors).forEach((field) => {
          const messages = Array.isArray(errorData.errors[field])
            ? errorData.errors[field]
            : [errorData.errors[field]];
          const message = messages.find(Boolean);

          if (message) {
            this.$set(this.filterErrors, field, message);
          }
        });
      }

      const firstFieldError = Object.values(this.filterErrors).find(Boolean);
      const toastMessage =
        firstFieldError ||
        errorData.message ||
        this.$t("REPORTS.fetchError");

      this.$message.error(toastMessage);
    },

    // Start:: Validate Filter
    validateFilter() {
      this.clearFilterErrors();

      if (!this.filterOptions.period) {
        const message = this.$t("REPORTS.periodRequired");
        this.$set(this.filterErrors, "period", message);
        this.$message.error(message);
        return false;
      }

      if (this.filterOptions.period.value === "custom") {
        if (!this.filterOptions.from_date) {
          const message = this.$t("REPORTS.fromDateRequired");
          this.$set(this.filterErrors, "from_date", message);
          this.$message.error(message);
          return false;
        }
        if (!this.filterOptions.to_date) {
          const message = this.$t("REPORTS.toDateRequired");
          this.$set(this.filterErrors, "to_date", message);
          this.$message.error(message);
          return false;
        }

        const from = new Date(this.filterOptions.from_date);
        const to = new Date(this.filterOptions.to_date);
        if (from > to) {
          const message = this.$t("REPORTS.invalidDateRange");
          this.$set(this.filterErrors, "to_date", message);
          this.$message.error(message);
          return false;
        }

        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (to > today) {
          const message = this.$t("REPORTS.toDateFuture");
          this.$set(this.filterErrors, "to_date", message);
          this.$message.error(message);
          return false;
        }
      }

      return true;
    },
    // End:: Validate Filter

    // Start:: Fetch Report
    async fetchReport() {
      if (!this.validateFilter()) {
        return;
      }

      this.loading = true;
      try {
        const params = {
          period: this.filterOptions.period?.value,
          from_date: this.filterOptions.from_date,
          to_date: this.filterOptions.to_date,
          driver_id: this.filterOptions.driver?.id || undefined,
          city: this.filterOptions.city || undefined,
        };

        const res = await this.$axios({
          method: "GET",
          url: "reports",
          params: params,
        });

        this.reportData = res.data.data || res.data;

        this.reportLoaded = true;
        this.chartKey++;
      } catch (error) {
        this.handleFilterApiError(error);
      } finally {
        this.loading = false;
      }
    },
    // End:: Fetch Report

    // Start:: Export Reporta
    async exportReport(format) {
      if (!this.hasData) {
        this.$message.error(this.$t("REPORTS.noDataToExport"));
        return;
      }

      this.exporting = true;
      try {
        const params = {
          period: this.filterOptions.period?.value || undefined,
          from_date: this.filterOptions.from_date || undefined,
          to_date: this.filterOptions.to_date || undefined,
          driver_id: this.filterOptions.driver?.id || undefined,
          city: this.filterOptions.city || undefined,
          format: format,
        };

        const res = await this.$axios({
          method: "GET",
          url: "reports/export",
          params: params,
          responseType: "blob",
        });

        const mimeTypes = {
          excel:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          pdf: "application/pdf",
        };

        const extensions = {
          excel: "xlsx",
          pdf: "pdf",
        };

        const blob = new Blob([res.data], {
          type: mimeTypes[format],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `report_${new Date().toISOString().slice(0, 10)}.${
            extensions[format]
          }`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$message.success(this.$t("REPORTS.exportSuccess"));
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("REPORTS.exportError")
        );
      } finally {
        this.exporting = false;
      }
    },
    // End:: Export Report

    // Start:: Reset Filters
    resetFilters() {
      this.clearFilterErrors();
      this.filterOptions = {
        period: this.getDefaultPeriod(),
        from_date: null,
        to_date: null,
        driver: null,
        city: null,
      };
      this.reportLoaded = false;
      this.reportData = {
        metrics: {
          total_packages: 0,
          delivered: 0,
          in_progress: 0,
          delayed: 0,
          completion_rate: 0,
          avg_delivery_time_hours: 0,
        },
        charts: {
          statuses_by_day: [],
          driver_performance: [],
          city_distribution: [],
        },
      };
      this.fetchReport();
    },
    // End:: Reset Filters
  },
};
</script>

<style lang="scss" scoped>
// ========================================
// Reports Page — Enhanced UI / Theming
// Uses project CSS-variables + SCSS mixins
// ========================================

// ─── Filter Validation ──────────────────
.filter_errors_summary {
  width: 100%;
  margin: 0 0 8px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(220, 53, 69, 0.25);
  background: rgba(220, 53, 69, 0.08);
  display: flex;
  align-items: flex-start;
  gap: 10px;

  i {
    color: var(--mid_red_clr);
    font-size: 18px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  ul {
    margin: 0;
    padding-inline-start: 18px;
    color: var(--mid_red_clr);
    font-size: 13px;
    font-family: $medium_font;

    li + li {
      margin-top: 4px;
    }
  }
}

.filter_field_col {
  &.has-error {
    ::v-deep .form-control,
    ::v-deep .multiselect__tags,
    ::v-deep .ant-calendar-picker-input {
      border-color: var(--mid_red_clr) !important;
    }
  }

  .filter_field_error {
    margin: -10px 0 4px;
    padding-inline: 4px;
    font-size: 12px;
    color: var(--mid_red_clr);
    font-family: $medium_font;
    line-height: 1.5;
  }
}

// ─── Buttons (export only) ──────────────
.reports_btn {
  padding: 10px 22px;
  border: none;
  @include borderRadius(8px);
  cursor: pointer;
  font-size: 14px;
  font-family: $semi_bold_font;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  position: relative;
  overflow: hidden;

  // ripple-like subtle glow on hover
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.12);
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover:not(:disabled)::after {
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--success {
    background-color: #28a745;
    color: #fff;

    &:hover:not(:disabled) {
      box-shadow: 0 4px 14px rgba(40, 167, 69, 0.35);
    }
  }

  &--danger {
    background-color: var(--mid_red_clr);
    color: #fff;

    &:hover:not(:disabled) {
      box-shadow: 0 4px 14px rgba(220, 53, 69, 0.35);
    }
  }
}

// ─── Alert Warning ──────────────────────
.alert_warning {
  background-color: var(--soft_yellow_clr);
  color: var(--gold_clr);
  border: 1px solid var(--gold_clr);
  @include borderRadius(10px);
  padding: 14px 20px;
  margin-bottom: 22px;
  font-size: 14px;
  font-family: $medium_font;
  display: flex;
  align-items: center;
  animation: slideDown 0.35s ease;

  i {
    font-size: 18px;
    margin-inline-end: 10px;
    flex-shrink: 0;
  }
}

// ─── Export Wrapper ─────────────────────
.export_wrapper {
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 576px) {
    justify-content: center;

    .reports_btn {
      flex: 1;
      min-width: 140px;
    }
  }
}

// ─── Statistics Grid ────────────────────
.stats_grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

// ─── Stat Card ──────────────────────────
.stat_card {
  background-color: var(--theme_second_bg_clr);
  @include borderRadius(14px);
  padding: 22px 20px;
  display: flex;
  align-items: center;
  gap: 18px;
  @include simpleShadow;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease;
  border-inline-start: 4px solid transparent;
  position: relative;
  overflow: hidden;

  // subtle decorative gradient overlay

  &:hover {
    transform: translateY(-4px);
    @include midShadow;

    &::before {
      opacity: 0.1;
    }
  }

  .stat_icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    @include flexCenterAlignment;
    flex-shrink: 0;
    transition: transform 0.3s ease;

    i {
      font-size: 22px;
      color: #fff;
    }
  }

  &:hover .stat_icon {
    transform: scale(1.08);
  }

  .stat_info {
    display: flex;
    flex-direction: column;
    min-width: 0;

    .stat_label {
      font-size: 12.5px;
      font-family: $medium_font;
      color: var(--theme_secondary_text_clr);
      margin-bottom: 4px;
      @include textEllipsis;
    }

    .stat_value {
      font-size: 26px;
      font-family: $bold_font;
      color: var(--theme_text_clr);
      line-height: 1.2;
    }
  }

  // ─ Card Variants
  &--total {
    border-inline-start-color: var(--main_theme_clr);

    &::before {
      background-color: var(--main_theme_clr);
    }

    .stat_icon {
      background: linear-gradient(135deg, #ef3405 0%, #ff6b3d 100%);
      box-shadow: 0 4px 12px rgba(239, 52, 5, 0.3);
    }
  }

  &--delivered {
    border-inline-start-color: #28a745;

    &::before {
      background-color: #28a745;
    }

    .stat_icon {
      background: linear-gradient(135deg, #28a745 0%, #5cb85c 100%);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  }

  &--progress {
    border-inline-start-color: #17a2b8;

    &::before {
      background-color: #17a2b8;
    }

    .stat_icon {
      background: linear-gradient(135deg, #17a2b8 0%, #3dd5f3 100%);
      box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
    }
  }

  &--delayed {
    border-inline-start-color: #ffc107;

    &::before {
      background-color: #ffc107;
    }

    .stat_icon {
      background: linear-gradient(135deg, #ffc107 0%, #ffda6a 100%);
      box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
    }
  }

  &--rate {
    border-inline-start-color: #6f42c1;

    &::before {
      background-color: #6f42c1;
    }

    .stat_icon {
      background: linear-gradient(135deg, #6f42c1 0%, #a370f7 100%);
      box-shadow: 0 4px 12px rgba(111, 66, 193, 0.3);
    }
  }

  &--time {
    border-inline-start-color: #fd7e14;

    &::before {
      background-color: #fd7e14;
    }

    .stat_icon {
      background: linear-gradient(135deg, #fd7e14 0%, #ffb97a 100%);
      box-shadow: 0 4px 12px rgba(253, 126, 20, 0.3);
    }
  }

  // ─ Responsive tweaks for stat card
  @media (max-width: 768px) {
    padding: 18px 16px;
    gap: 14px;

    .stat_icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;

      i {
        font-size: 18px;
      }
    }

    .stat_info {
      .stat_value {
        font-size: 22px;
      }
    }
  }

  @media (max-width: 480px) {
    padding: 16px 14px;

    .stat_info {
      .stat_label {
        font-size: 11.5px;
      }

      .stat_value {
        font-size: 20px;
      }
    }
  }
}

// ─── Charts Grid ────────────────────────
.charts_grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
}

.chart_wrapper {
  background-color: var(--theme_second_bg_clr);
  @include borderRadius(14px);
  padding: 24px 20px;
  @include simpleShadow;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  animation: fadeInUp 0.45s ease both;

  &:nth-child(1) {
    animation-delay: 0.05s;
  }
  &:nth-child(2) {
    animation-delay: 0.12s;
  }
  &:nth-child(3) {
    animation-delay: 0.19s;
  }

  &:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  &:hover {
    @include midShadow;
  }

  @media (max-width: 576px) {
    padding: 16px 12px;
  }
}

// ─── Loading State ──────────────────────
.reports_loading {
  background-color: var(--theme_second_bg_clr);
  @include borderRadius(14px);
  padding: 80px 24px;
  text-align: center;
  @include simpleShadow;
  animation: fadeInUp 0.35s ease both;

  i {
    font-size: 40px;
    color: var(--main_theme_clr);
    margin-bottom: 16px;
    display: block;
  }

  p {
    font-size: 15px;
    font-family: $medium_font;
    color: var(--theme_secondary_text_clr);
    margin: 0;
  }
}

// ─── No Data Message ────────────────────
.no_data_message {
  background-color: var(--theme_second_bg_clr);
  @include borderRadius(14px);
  padding: 56px 24px 64px;
  text-align: center;
  @include simpleShadow;
  transition: background-color 0.3s ease;
  animation: fadeInUp 0.45s ease both;

  .no_data_illustration {
    width: 180px;
    max-width: 60%;
    height: auto;
    margin: 0 auto 24px;
    display: block;
    opacity: 0.85;
  }

  .no_data_title {
    font-size: 18px;
    font-family: $semi_bold_font;
    color: var(--theme_text_clr);
    margin: 0 0 8px;
  }

  .no_data_hint {
    font-size: 14px;
    font-family: $medium_font;
    color: var(--theme_secondary_text_clr);
    margin: 0;
    max-width: 420px;
    margin-inline: auto;
    line-height: 1.6;
  }
}

// ─── Report Content Entrance ────────────
.reports_content {
  animation: fadeInUp 0.4s ease both;
}

// ─── Staggered stat card animations ─────
.stats_grid {
  .stat_card {
    animation: fadeInUp 0.4s ease both;

    @for $i from 1 through 6 {
      &:nth-child(#{$i}) {
        animation-delay: #{$i * 0.06}s;
      }
    }
  }
}

// ─── Keyframes ──────────────────────────
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
